import React, { useMemo, useState, useEffect, useContext } from "react";
import {
  Card,
  Container,
  Typography,
  Box,
  IconButton,
  Tooltip,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  TableSortLabel,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { HighlightOff } from "@mui/icons-material";

import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
  createColumnHelper,
  SortingState,
} from "@tanstack/react-table";

import { getAllEndTenancyNotices } from "../../apis/property/tenancy";
import { showSnackBar } from "../../functions/snackbar";
import { AppCtx } from "../../store/store";

export interface TenanacyContract {
  id: string;
  propertyName: string;
  tenantName: string;
  endNoticeDate: string;
  propertyId: number;
}

const columnHelper = createColumnHelper<TenanacyContract>();

export const PendingMoveOuts: React.FC = () => {
  const [tenancyContracts, setTenancyContracts] = useState<TenanacyContract[]>(
    []
  );
  const [sorting, setSorting] = useState<SortingState>([]);
  const { setIsLoading, getLoggedUser } = useContext(AppCtx);
  const loggedUser = getLoggedUser();
  const navigate = useNavigate();

  const fetchTenancyEndNotices = async () => {
    if (!loggedUser) return;
    setIsLoading(true);
    try {
      const response = await getAllEndTenancyNotices(loggedUser.token, true);
      setTenancyContracts(response as unknown as TenanacyContract[]);
    } catch (err: any) {
      showSnackBar("error", err.message || err);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchTenancyEndNotices();
  }, []);

  const columns = useMemo(
    () => [
      columnHelper.accessor("propertyName", {
        header: "Property Name",
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor("tenantName", {
        header: "Tenant Name",
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor("endNoticeDate", {
        header: "Notice End Date",
        cell: (info) => new Date(info.getValue()).toLocaleDateString(),
      }),
    ],
    []
  );

  const table = useReactTable({
    data: tenancyContracts,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  return (
    <Container maxWidth="md">
      <Card variant="outlined" sx={{ borderRadius: 3 }}>
        <TableContainer component={Paper} sx={{ p: 2, height: "60vh", m: 2 }}>
          <Typography variant="h4" gutterBottom>
            Pending Move Out's
          </Typography>

          <Table>
            <TableHead>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableCell key={header.id}>
                      {header.isPlaceholder ? null : (
                        <TableSortLabel
                          active={!!header.column.getIsSorted()}
                          direction={
                            header.column.getIsSorted() === "desc"
                              ? "desc"
                              : "asc"
                          }
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </TableSortLabel>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableHead>

            <TableBody>
              {table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}

              {table.getRowModel().rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center">
                    No end‑notice contracts found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination Controls */}
        <Box display="flex" justifyContent="center">
          <TablePagination
            component="div"
            count={tenancyContracts.length}
            page={table.getState().pagination.pageIndex}
            onPageChange={(_, newPage) => table.setPageIndex(newPage)}
            rowsPerPage={table.getState().pagination.pageSize}
            onRowsPerPageChange={(e) => {
              table.setPageSize(Number(e.target.value));
            }}
            rowsPerPageOptions={[5, 10, 25]}
          />
        </Box>

        <Box display="flex" justifyContent="center">
          <Tooltip title={"Back to property page"}>
            <IconButton
              color="primary"
              onClick={() => navigate("/properties/")}
            >
              <HighlightOff sx={{ fontSize: 60 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Card>
    </Container>
  );
};
